import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  createLoan,
  deleteLoan,
  fetchCompanies,
  fetchLoans,
  patchLoan,
  type Company,
  type Loan,
} from "../api";
import { formatDateEs, formatMoney } from "../lib/grouping";

const NO_COMPANY_KEY = "__none__";
const NO_COMPANY_LABEL = "— Sin empresa —";

type LoanGroup = {
  key: string;
  label: string;
  loans: Loan[];
  totalGranted: number;
  totalPending: number;
  currency: string;
};

function groupByCompany(items: Loan[], companies: Company[]): LoanGroup[] {
  const byId = new Map(companies.map((c) => [c.id, c.alias || c.name] as const));
  const buckets = new Map<string, LoanGroup>();
  for (const l of items) {
    const key = l.companyId || NO_COMPANY_KEY;
    const label = byId.get(l.companyId) ?? (l.companyId || NO_COMPANY_LABEL);
    let g = buckets.get(key);
    if (!g) {
      g = { key, label, loans: [], totalGranted: 0, totalPending: 0, currency: l.currency || "EUR" };
      buckets.set(key, g);
    }
    g.loans.push(l);
    if (l.lastGrantedCapital !== null) g.totalGranted += l.lastGrantedCapital;
    if (l.lastPendingAmount !== null) g.totalPending += l.lastPendingAmount;
  }
  const groups = Array.from(buckets.values());
  groups.sort((a, b) => a.label.localeCompare(b.label, "es"));
  for (const g of groups) {
    g.loans.sort((a, b) =>
      (a.bankCode + a.contractNumber).localeCompare(b.bankCode + b.contractNumber, "es"),
    );
  }
  return groups;
}

export function Loans() {
  // Activa layout ancho (main sin max-width 1400) mientras la vista está
  // montada. Consistente con la pantalla de Leasings.
  useEffect(() => {
    document.body.classList.add("main-wide");
    return () => document.body.classList.remove("main-wide");
  }, []);
  const [items, setItems] = useState<Loan[] | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({
    bankCode: "",
    contractNumber: "",
    bankName: "",
    companyId: "",
    alias: "",
    currency: "EUR",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setError(null);
    try {
      const [l, c] = await Promise.all([fetchLoans(), fetchCompanies()]);
      setItems(l.items);
      setCompanies(c.items);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const groups = useMemo(
    () => (items ? groupByCompany(items, companies) : null),
    [items, companies],
  );

  const save = async () => {
    if (!draft.bankCode.trim() || !draft.contractNumber.trim() || !draft.companyId) {
      alert("Indica banco, número de contrato y empresa.");
      return;
    }
    setBusy(true);
    try {
      await createLoan({
        bankCode: draft.bankCode,
        contractNumber: draft.contractNumber,
        bankName: draft.bankName.trim() || undefined,
        companyId: draft.companyId,
        alias: draft.alias.trim() || undefined,
        currency: draft.currency,
        notes: draft.notes.trim() || undefined,
      });
      setDraft({
        bankCode: "",
        contractNumber: "",
        bankName: "",
        companyId: "",
        alias: "",
        currency: "EUR",
        notes: "",
      });
      setCreating(false);
      load();
    } catch (e: unknown) {
      alert(`Error al crear: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>Préstamos</h2>
          <p className="meta">
            Da de alta cada préstamo con su contrato, banco y empresa.
            Capital pendiente y vencimientos se sincronizan automáticamente.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button type="button" onClick={() => setCreating((x) => !x)} disabled={companies.length === 0}>
            {creating ? "✕ Cancelar" : "+ Nuevo préstamo"}
          </button>
          <button type="button" onClick={load}>Recargar</button>
        </div>
      </div>

      {companies.length === 0 && (
        <p className="warning">
          No hay empresas. Crea al menos una en "Empresas" para asociar préstamos.
        </p>
      )}

      {creating && companies.length > 0 && (
        <div className="form-row" style={{ flexWrap: "wrap" }}>
          <input placeholder="bankCode" value={draft.bankCode} onChange={(e) => setDraft({ ...draft, bankCode: e.target.value })} />
          <input placeholder="Nº contrato" value={draft.contractNumber} onChange={(e) => setDraft({ ...draft, contractNumber: e.target.value })} />
          <input placeholder="Banco (nombre)" value={draft.bankName} onChange={(e) => setDraft({ ...draft, bankName: e.target.value })} />
          <select value={draft.companyId} onChange={(e) => setDraft({ ...draft, companyId: e.target.value })}>
            <option value="">— Empresa —</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.alias || c.name} ({c.id})</option>
            ))}
          </select>
          <input placeholder="Alias" value={draft.alias} onChange={(e) => setDraft({ ...draft, alias: e.target.value })} />
          <input placeholder="EUR" value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })} style={{ width: "5rem" }} />
          <button type="button" onClick={save} disabled={busy}>{busy ? "Guardando…" : "Guardar"}</button>
        </div>
      )}

      {error && <p className="error">{error}</p>}
      {items === null && !error && <p>Cargando…</p>}
      {items && items.length === 0 && <p className="meta">No hay préstamos dados de alta.</p>}
      {groups && groups.length > 0 && (
        <table className="data">
          <thead>
            <tr>
              <th>Banco</th>
              <th>Contrato</th>
              <th>Alias</th>
              <th className="num">Concedido</th>
              <th className="num" title="Capital pendiente total. Tooltip por fila: desglose CP (próximos 12m) / LP (>12m).">Pendiente</th>
              <th title="Siguiente cuota a pagar del cuadro (fecha e importe)">Próx. cuota</th>
              <th>Vencimiento</th>
              <th>Último sync</th>
              <th />
            </tr>
          </thead>
          {groups.map((g) => (
            <tbody key={g.key}>
              <tr className="group-head">
                <td colSpan={9}>
                  <div className="group-head-row">
                    <span className="group-name">{g.label}</span>
                    <span className="group-totals">
                      <span>
                        <span className="lbl">Concedido</span>{" "}
                        {formatMoney(g.totalGranted, g.currency)}
                      </span>
                      <span>
                        <span className="lbl">Pendiente</span>{" "}
                        {formatMoney(g.totalPending, g.currency)}
                      </span>
                      <span className="badge">
                        {g.loans.length} {g.loans.length === 1 ? "préstamo" : "préstamos"}
                      </span>
                    </span>
                  </div>
                </td>
              </tr>
              {g.loans.map((l) => (
                <LoanRow
                  key={`${l.bankCode}::${l.contractNumber}`}
                  loan={l}
                  companies={companies}
                  onUpdated={load}
                />
              ))}
            </tbody>
          ))}
        </table>
      )}
    </section>
  );
}

/** Devuelve true si la cuenta contable requiere centro de coste
 *  (grupos 6 y 7 del PGC: gastos e ingresos). Debe coincidir con
 *  accountRequiresCostingCode() del backend. */
function accountRequiresCostingCode(account: string): boolean {
  const s = (account ?? "").trim();
  return s.length > 0 && (s.startsWith("6") || s.startsWith("7"));
}

type LoanAccountsDraft = {
  sapAccountLongTerm: string;
  costingCodeLongTerm: string;
  sapAccountShortTerm: string;
  costingCodeShortTerm: string;
  sapAccountInterest: string;
  costingCodeInterest: string;
  sapAccountFees: string;
  costingCodeFees: string;
  sapAccountBridge: string;
  costingCodeBridge: string;
};

function LoanRow({
  loan,
  companies,
  onUpdated,
}: {
  loan: Loan;
  companies: Company[];
  onUpdated: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [alias, setAlias] = useState(loan.alias);
  const [companyId, setCompanyId] = useState(loan.companyId);
  const [descriptionPattern, setDescriptionPattern] = useState(loan.descriptionPattern);
  const [accDraft, setAccDraft] = useState<LoanAccountsDraft>({
    sapAccountLongTerm: loan.sapAccountLongTerm,
    costingCodeLongTerm: loan.costingCodeLongTerm,
    sapAccountShortTerm: loan.sapAccountShortTerm,
    costingCodeShortTerm: loan.costingCodeShortTerm,
    sapAccountInterest: loan.sapAccountInterest,
    costingCodeInterest: loan.costingCodeInterest,
    sapAccountFees: loan.sapAccountFees,
    costingCodeFees: loan.costingCodeFees,
    sapAccountBridge: loan.sapAccountBridge,
    costingCodeBridge: loan.costingCodeBridge,
  });
  const [busy, setBusy] = useState(false);

  const startEditing = () => {
    setAlias(loan.alias);
    setCompanyId(loan.companyId);
    setDescriptionPattern(loan.descriptionPattern);
    setAccDraft({
      sapAccountLongTerm: loan.sapAccountLongTerm,
      costingCodeLongTerm: loan.costingCodeLongTerm,
      sapAccountShortTerm: loan.sapAccountShortTerm,
      costingCodeShortTerm: loan.costingCodeShortTerm,
      sapAccountInterest: loan.sapAccountInterest,
      costingCodeInterest: loan.costingCodeInterest,
      sapAccountFees: loan.sapAccountFees,
      costingCodeFees: loan.costingCodeFees,
      sapAccountBridge: loan.sapAccountBridge,
      costingCodeBridge: loan.costingCodeBridge,
    });
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      await patchLoan(loan.bankCode, loan.contractNumber, {
        alias,
        companyId,
        descriptionPattern,
        ...accDraft,
      });
      setEditing(false);
      onUpdated();
    } catch (e: unknown) {
      alert(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!confirm(`¿Borrar el préstamo ${loan.contractNumber}?`)) return;
    setBusy(true);
    try {
      await deleteLoan(loan.bankCode, loan.contractNumber);
      onUpdated();
    } catch (e: unknown) {
      alert(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  // Vista compacta de las cuentas en la fila principal (cuando no se edita)
  // — muestra el status en una sola cadena para no ampliar la tabla.
  const accountsSummary = ((): string => {
    const parts: string[] = [];
    if (loan.sapAccountLongTerm) parts.push(`LP ${loan.sapAccountLongTerm}`);
    if (loan.sapAccountShortTerm) parts.push(`CP ${loan.sapAccountShortTerm}`);
    if (loan.sapAccountInterest) parts.push(`Int ${loan.sapAccountInterest}`);
    if (loan.sapAccountFees) parts.push(`Com ${loan.sapAccountFees}`);
    return parts.join(" · ");
  })();

  return (
    <>
      <tr style={loan.active ? undefined : { opacity: 0.5 }}>
        <td style={{ whiteSpace: "nowrap" }}>{loan.bankCode}</td>
        <td style={{ whiteSpace: "nowrap" }}>{loan.contractNumber}</td>
        <td>
          {editing
            ? <input value={alias} onChange={(e) => setAlias(e.target.value)} />
            : (loan.alias || <span className="meta">—</span>)}
        </td>
        <td className="num" style={{ whiteSpace: "nowrap" }}>{loan.lastGrantedCapital !== null ? formatMoney(loan.lastGrantedCapital, loan.currency) : <span className="meta">—</span>}</td>
        {/* Pendiente: importe total del banco arriba; desglose CP/LP calculado
            del cuadro debajo como meta (visible siempre). */}
        <td className="num" style={{ whiteSpace: "nowrap" }}>
          <div>
            {loan.lastPendingAmount !== null ? formatMoney(loan.lastPendingAmount, loan.currency) : <span className="meta">—</span>}
          </div>
          {loan.summary && (loan.summary.pendingCapitalShortTerm > 0 || loan.summary.pendingCapitalLongTerm > 0) && (
            <div className="meta" style={{ fontSize: "0.75em" }}>
              CP {formatMoney(loan.summary.pendingCapitalShortTerm, loan.currency)}
              {" · "}
              LP {formatMoney(loan.summary.pendingCapitalLongTerm, loan.currency)}
            </div>
          )}
        </td>
        {/* Próxima cuota: fecha e importe en una sola línea con separador ·. */}
        <td style={{ whiteSpace: "nowrap" }}>
          {loan.summary?.nextInstallmentDate ? (
            <>
              {formatDateEs(loan.summary.nextInstallmentDate.slice(0, 10))}
              {loan.summary.nextInstallmentAmount !== null && (
                <> · {formatMoney(loan.summary.nextInstallmentAmount, loan.currency)}</>
              )}
            </>
          ) : (
            <span className="meta">—</span>
          )}
        </td>
        <td style={{ whiteSpace: "nowrap" }}>{loan.lastExpirationDate || <span className="meta">—</span>}</td>
        <td style={{ whiteSpace: "nowrap" }}>{loan.lastSyncedAt ? new Date(loan.lastSyncedAt).toLocaleString() : <span className="meta">nunca</span>}</td>
        <td className="actions" style={{ whiteSpace: "nowrap" }}>
          {editing ? (
            <>
              <button type="button" onClick={save} disabled={busy} style={{ fontSize: "0.8em", padding: "2px 8px" }}>OK</button>
              <button type="button" onClick={() => setEditing(false)} disabled={busy} style={{ fontSize: "0.8em", padding: "2px 6px" }}>✕</button>
            </>
          ) : (
            <>
              <Link
                to={`/prestamos/${encodeURIComponent(loan.bankCode)}/${encodeURIComponent(loan.contractNumber)}/amortizacion`}
              >
                <button type="button" style={{ fontSize: "0.8em", padding: "2px 8px" }}>Cuadro</button>
              </Link>
              <button type="button" onClick={startEditing} title="Editar alias, empresa y cuentas contables" style={{ fontSize: "0.8em", padding: "2px 6px" }}>✏️</button>
              <button type="button" className="danger" onClick={onDelete} disabled={busy} style={{ fontSize: "0.8em", padding: "2px 6px" }}>🗑️</button>
            </>
          )}
        </td>
      </tr>
      {!editing && loan.descriptionPattern && (
        <tr style={{ ...(loan.active ? {} : { opacity: 0.5 }), background: "rgba(80,160,255,0.04)" }}>
          <td colSpan={9} style={{ fontSize: "0.8em", padding: "0.15rem 0.75rem", color: "var(--meta, #888)" }}>
            🔍 Patrón detección: <code>{loan.descriptionPattern}</code>
          </td>
        </tr>
      )}
      {!editing && accountsSummary && (
        <tr style={{ ...(loan.active ? {} : { opacity: 0.5 }), background: "rgba(80,160,255,0.04)" }}>
          <td colSpan={9} style={{ fontSize: "0.8em", padding: "0.15rem 0.75rem", color: "var(--meta, #888)" }}>
            🧾 Cuentas contables: {accountsSummary}
          </td>
        </tr>
      )}
      {editing && (
        <tr>
          <td colSpan={9} style={{ background: "rgba(80,160,255,0.06)", padding: "0.75rem 1rem" }}>
            <div style={{ marginBottom: "0.75rem" }}>
              <h4 style={{ margin: "0 0 0.35rem 0", fontSize: "0.95em" }}>Empresa</h4>
              <select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.alias || c.name}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: "0.75rem" }}>
              <h4 style={{ margin: "0 0 0.35rem 0", fontSize: "0.95em" }}>
                Patrón de detección
              </h4>
              <p className="meta" style={{ margin: "0 0 0.4rem 0", fontSize: "0.85em" }}>
                Texto que aparece en la descripción del movimiento del banco
                cuando se paga una cuota de este préstamo (case-insensitive).
                Si está relleno, sustituye al matching por contractNumber.
              </p>
              <input
                type="text"
                value={descriptionPattern}
                onChange={(e) => setDescriptionPattern(e.target.value)}
                placeholder="ej. LIQUID. CUOTA PTMO. 510008642"
                style={{ width: "100%", maxWidth: 480 }}
              />
            </div>
            <LoanAccountsEditor draft={accDraft} onChange={setAccDraft} />
          </td>
        </tr>
      )}
    </>
  );
}

function LoanAccountsEditor({
  draft,
  onChange,
}: {
  draft: LoanAccountsDraft;
  onChange: (d: LoanAccountsDraft) => void;
}) {
  const rows: Array<{
    label: string;
    hint: string;
    accKey: keyof LoanAccountsDraft;
    ccKey: keyof LoanAccountsDraft;
  }> = [
    { label: "Deuda largo plazo", hint: "p.ej. 170", accKey: "sapAccountLongTerm", ccKey: "costingCodeLongTerm" },
    { label: "Deuda corto plazo", hint: "p.ej. 520", accKey: "sapAccountShortTerm", ccKey: "costingCodeShortTerm" },
    { label: "Intereses financieros", hint: "p.ej. 662", accKey: "sapAccountInterest", ccKey: "costingCodeInterest" },
    { label: "Comisiones / otros gastos", hint: "p.ej. 669", accKey: "sapAccountFees", ccKey: "costingCodeFees" },
    { label: "Puente cuotas parciales", hint: "p.ej. 555600 (compartida)", accKey: "sapAccountBridge", ccKey: "costingCodeBridge" },
  ];
  return (
    <div>
      <h4 style={{ margin: "0 0 0.5rem 0", fontSize: "0.95em" }}>Cuentas contables del préstamo</h4>
      <p className="meta" style={{ margin: "0 0 0.75rem 0", fontSize: "0.85em" }}>
        Se usarán al contabilizar los pagos de este préstamo. Las cuentas que
        empiezan por 6 o 7 requieren centro de coste.
      </p>
      <table style={{ width: "100%", fontSize: "0.85em" }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left" }}>Concepto</th>
            <th style={{ textAlign: "left" }}>Cuenta SAP</th>
            <th style={{ textAlign: "left" }}>Centro de coste</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const acc = draft[r.accKey];
            const cc = draft[r.ccKey];
            const requiresCC = accountRequiresCostingCode(acc);
            const missingCC = requiresCC && !cc.trim();
            return (
              <tr key={r.accKey}>
                <td style={{ padding: "0.15rem 0.5rem 0.15rem 0" }}>{r.label}</td>
                <td style={{ padding: "0.15rem 0.5rem 0.15rem 0" }}>
                  <input
                    placeholder={r.hint}
                    value={acc}
                    onChange={(e) => onChange({ ...draft, [r.accKey]: e.target.value })}
                    style={{ width: "10rem" }}
                  />
                </td>
                <td style={{ padding: "0.15rem 0.5rem 0.15rem 0" }}>
                  <input
                    placeholder={requiresCC ? "obligatorio" : "opcional"}
                    value={cc}
                    onChange={(e) => onChange({ ...draft, [r.ccKey]: e.target.value })}
                    style={{
                      width: "8rem",
                      borderColor: missingCC ? "#c62828" : undefined,
                    }}
                  />
                  {missingCC && (
                    <span style={{ color: "#c62828", marginLeft: 6, fontSize: "0.9em" }}>
                      ⚠ requerido
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
